import { computed } from '@preact/signals';
import { animals, schedules, openTimers, recentLogs, nowTick } from '@/state/data';
import { computeNextDue, dueState, type DueState } from '@/lib/due';
import type { Animal, Schedule, Timer, FeedLog } from '@/types';

export interface BoardEntry {
  schedule: Schedule;
  animal: Animal;
  nextDue: Date | null;
  state: DueState;
}

/** The Now board, computed fully client-side so it works offline.
 *  next_due_at from the server is used when fresher; otherwise recomputed. */
export const board = computed<BoardEntry[]>(() => {
  void nowTick.value; // re-evaluate on tick
  const now = new Date();
  const out: BoardEntry[] = [];
  for (const s of schedules.value.values()) {
    if (!s.enabled) continue;
    const a = animals.value.get(s.animal_id);
    if (!a || a.status !== 'active') continue;
    const nextDue = computeNextDue(s, now);
    out.push({ schedule: s, animal: a, nextDue, state: dueState(nextDue, now) });
  }
  out.sort((x, y) => {
    const xt = x.nextDue?.getTime() ?? Infinity;
    const yt = y.nextDue?.getTime() ?? Infinity;
    return xt - yt;
  });
  return out;
});

export const overdue = computed(() => board.value.filter((e) => e.state === 'overdue'));
export const dueSoon = computed(() => board.value.filter((e) => e.state === 'due_soon'));
export const scheduled = computed(() => board.value.filter((e) => e.state === 'scheduled'));
export const overdueCount = computed(() => overdue.value.length);

export const dueTimers = computed<Timer[]>(() => {
  void nowTick.value;
  return openTimers.value;
});

/** Feedings in the last 45 minutes — the "Just fed" rail. */
export const justFed = computed<{ log: FeedLog; animal: Animal }[]>(() => {
  void nowTick.value;
  const cutoff = Date.now() - 45 * 60_000;
  const seen = new Set<string>();
  const out: { log: FeedLog; animal: Animal }[] = [];
  for (const log of recentLogs.value) {
    if (new Date(log.fed_at).getTime() < cutoff) break; // recentLogs is sorted desc
    if (log.skipped || seen.has(log.animal_id)) continue;
    const a = animals.value.get(log.animal_id);
    if (!a) continue;
    seen.add(log.animal_id);
    out.push({ log, animal: a });
  }
  return out;
});

export const activeCount = computed(
  () => new Set(board.value.map((e) => e.animal.id)).size
);
